// Хроника цикла: лента событий, модалки глав/событий, литературный
// текст, комикс-вьюер мира. Вынесено из CycleTab.tsx без изменения
// поведения (Фаза 1, разбор монолита).

import { useState, useEffect, useMemo } from "react";
import {
  api,
  type ChronicleEvent,
  type ChronicleChapter,
  type CycleAction,
  type MediaAsset,
  type WorldChange,
} from "../api";
import { useAssetContent } from "./NegotiationRooms";
import {
  CycleImages,
  GAME_UI_ASSET_BASE,
  GameAssetIcon,
  Paragraphs,
  buildChronicleShowcaseItems,
  compactChangeLabel,
  formatDateTime,
  type ChronicleFeedItem,
  type Props,
} from "./shared";

function WorldComicStrip({
  campaignId,
  prologueAssets,
  recapAssets,
  label,
}: {
  campaignId: string;
  prologueAssets: MediaAsset[];
  recapAssets: MediaAsset[];
  label: string;
}) {
  const assets = recapAssets.length > 0 ? recapAssets : prologueAssets;
  const visible = assets.slice(0, 6);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  if (visible.length === 0) {
    return (
      <div className="cc-world-comic-placeholder">
        <img src={`${GAME_UI_ASSET_BASE}/icon_room_chronicle.png`} alt="" />
        <strong>Комикс ещё формируется</strong>
        <span>Итоги дня появятся здесь после публикации главы.</span>
      </div>
    );
  }
  return (
    <>
      <div className="cc-world-comic-strip" aria-label={label}>
        {visible.map((asset, index) => (
          <WorldComicThumb
            campaignId={campaignId}
            asset={asset}
            index={index + 1}
            key={asset.id}
            onOpen={() => setSelectedIndex(index)}
          />
        ))}
      </div>
      {selectedIndex !== null && (
        <WorldComicLightbox
          campaignId={campaignId}
          assets={visible}
          index={selectedIndex}
          label={label}
          onSelect={setSelectedIndex}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </>
  );
}
function WorldComicThumb({
  campaignId,
  asset,
  index,
  onOpen,
}: {
  campaignId: string;
  asset: MediaAsset;
  index: number;
  onOpen: () => void;
}) {
  const src = useAssetContent(campaignId, asset);
  return (
    <button
      type="button"
      className={`cc-world-comic-thumb ${src ? "ready" : asset.status.toLowerCase()}`}
      onClick={src ? onOpen : undefined}
      disabled={!src}
    >
      {src ? <img src={src} alt={`Комикс, страница ${index}`} /> : <span>{asset.status === "FAILED" ? "Ошибка" : "Готовится"}</span>}
      <b>{index}</b>
    </button>
  );
}
function WorldComicLightbox({
  campaignId,
  assets,
  index,
  label,
  onSelect,
  onClose,
}: {
  campaignId: string;
  assets: MediaAsset[];
  index: number;
  label: string;
  onSelect: (index: number) => void;
  onClose: () => void;
}) {
  const asset = assets[index];
  const src = useAssetContent(campaignId, asset);
  return (
    <div className="cc-modal-backdrop cc-chronicle-modal-backdrop cc-comic-lightbox-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-comic-lightbox-card">
        <header className="cc-modal-header cc-chronicle-modal-header-minimal">
          <div>
            <span>{label}</span>
            <h2>Страница {index + 1} из {assets.length}</h2>
          </div>
          <button type="button" className="cc-modal-close" onClick={onClose}>×</button>
        </header>
        <div className="cc-comic-lightbox-stage">
          <button type="button" onClick={() => onSelect(Math.max(0, index - 1))} disabled={index === 0}>‹</button>
          <div className="cc-comic-lightbox-image">
            {src ? <img src={src} alt={`Комикс, страница ${index + 1}`} /> : <span>Изображение загружается</span>}
          </div>
          <button type="button" onClick={() => onSelect(Math.min(assets.length - 1, index + 1))} disabled={index >= assets.length - 1}>›</button>
        </div>
        <div className="cc-comic-lightbox-dots">
          {assets.map((item, itemIndex) => (
            <button
              type="button"
              className={itemIndex === index ? "active" : ""}
              onClick={() => onSelect(itemIndex)}
              key={item.id}
            >
              {itemIndex + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
function ChronicleSection({ campaignId, setup, action }: Props) {
  const cycle = setup.cycle.current!;
  const [selectedItem, setSelectedItem] = useState<ChronicleFeedItem | null>(null);
  const showcaseItems = useMemo(
    () => buildChronicleShowcaseItems(setup, cycle),
    [setup.world, setup.cycle.chronicleChapters, cycle.id, cycle.situationSummary, cycle.closingNarrative, cycle.openingImages, cycle.chronicleComicRecapImages, cycle.worldChanges],
  );
  const [activeIndex, setActiveIndex] = useState(() => Math.max(0, showcaseItems.length - 1));

  useEffect(() => {
    setActiveIndex(Math.max(0, showcaseItems.length - 1));
  }, [showcaseItems.length]);

  useEffect(() => {
    if (cycle.unread.chronicleCount > 0)
      void api.markRead(campaignId, "CHRONICLE").catch(() => undefined);
  }, [campaignId, cycle.id, cycle.unread.chronicleCount]);

  const activeItem = showcaseItems[activeIndex];

  return (
    <div className="cc-cycle-section-stack cc-room-page cc-chronicle-room-page cc-chronicle-feed-page cc-chronicle-carousel-page">
      <section className="cc-chronicle-feed-panel cc-chronicle-carousel-panel">
        <div className="cc-chronicle-feed-heading">
          <GameAssetIcon src={chronicleEventIconSrc("DAY_OUTCOME")} fallback={chronicleEventIcon("DAY_OUTCOME")} />
          <div>
            <small>Хроника кампании</small>
            <h2>Комикс по главам</h2>
          </div>
        </div>

        {showcaseItems.length === 0 ? (
          <p className="cc-empty-inline">
            Комикс ещё не сформирован. Сначала появится пролог, затем главы по итогам каждого дня.
          </p>
        ) : (
          <>
            <div className="cc-chronicle-carousel-nav">
              <button type="button" onClick={() => setActiveIndex((current) => Math.max(0, current - 1))} disabled={activeIndex === 0}>‹</button>
              <div className="cc-chronicle-carousel-dots" role="tablist" aria-label="Главы хроники">
                {showcaseItems.map((item, index) => (
                  <button
                    type="button"
                    key={item.id}
                    className={index === activeIndex ? "active" : ""}
                    onClick={() => setActiveIndex(index)}
                  >
                    <small>{item.meta}</small>
                    <strong>{item.shortLabel}</strong>
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setActiveIndex((current) => Math.min(showcaseItems.length - 1, current + 1))} disabled={activeIndex === showcaseItems.length - 1}>›</button>
            </div>

            {activeItem && (
              <article className="cc-chronicle-carousel-stage">
                <header>
                  <small>{activeItem.meta}</small>
                  <h3>{activeItem.title}</h3>
                  <p>{activeItem.summaryText}</p>
                </header>

                <WorldComicStrip
                  campaignId={campaignId}
                  prologueAssets={activeItem.images}
                  recapAssets={activeItem.images}
                  label={activeItem.title}
                />

                <div className="cc-chronicle-carousel-info">
                  <section className="cc-world-panel">
                    <div className="cc-world-panel-title"><img src={`${GAME_UI_ASSET_BASE}/icon_chronicle_situation.png`} alt="" /><h3>{activeItem.situationHeading}</h3></div>
                    <p>{activeItem.situationText}</p>
                  </section>
                  <section className="cc-world-panel">
                    <div className="cc-world-panel-title"><img src={`${GAME_UI_ASSET_BASE}/icon_room_chronicle.png`} alt="" /><h3>{activeItem.outcomeHeading}</h3></div>
                    <p>{activeItem.outcomeText}</p>
                    {activeItem.changes.length > 0 && (
                      <div className="cc-world-change-grid">
                        {activeItem.changes.slice(0, 6).map((change) => (
                          <span className={change.delta >= 0 ? "positive" : "negative"} key={`${change.kind}-${change.code}-${change.after}`}>
                            <b>{compactChangeLabel(change.displayName)}</b>
                            <i>{change.before} → {change.after}</i>
                          </span>
                        ))}
                      </div>
                    )}
                  </section>
                </div>

                <div className="cc-inline-actions">
                  <button type="button" onClick={() => setSelectedItem(activeItem.feedItem)}>Открыть главу</button>
                </div>
              </article>
            )}
          </>
        )}
      </section>

      {selectedItem && (
        <ChronicleEntryModal
          campaignId={campaignId}
          item={selectedItem}
          canManage={setup.canManage}
          action={action}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
}
function chronicleFeedMeta(item: ChronicleFeedItem): string {
  if (item.kind === "chapter") return `Глава ${item.chapter.cycleNumber}`;
  if (item.kind === "prologue") return "Пролог";
  if (item.kind === "cycle-situation") return "Ситуация дня";
  if (item.kind === "cycle-outcome") return "Итог дня";
  if (item.kind === "event") return chronicleStageLabel(item.event);
  return "Хроника";
}
function ChronicleEntryModal({
  campaignId,
  item,
  canManage,
  action,
  onClose,
}: {
  campaignId: string;
  item: ChronicleFeedItem;
  canManage: boolean;
  action: Props["action"];
  onClose: () => void;
}) {
  return (
    <div className="cc-modal-backdrop cc-chronicle-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-chronicle-modal-card">
        <header className="cc-modal-header cc-chronicle-modal-header-minimal">
          <button type="button" className="cc-modal-close" onClick={onClose}>
            ×
          </button>
        </header>
        <div className="cc-chronicle-modal-body">
          {item.kind === "chapter" ? (
            <ChronicleChapterDetails
              campaignId={campaignId}
              chapter={item.chapter}
              canManage={canManage}
              action={action}
            />
          ) : item.kind === "event" ? (
            <ChronicleEventDetails
              campaignId={campaignId}
              event={item.event}
              sourceAction={item.sourceAction}
              canManage={canManage}
              action={action}
            />
          ) : (
            <ChronicleSyntheticDetails
              campaignId={campaignId}
              item={item}
              canManage={canManage}
              action={action}
            />
          )}
        </div>
      </div>
    </div>
  );
}
function ChronicleEventDetails({
  campaignId,
  event,
  sourceAction,
  canManage,
  action,
}: {
  campaignId: string;
  event: ChronicleEvent;
  sourceAction?: CycleAction;
  canManage: boolean;
  action: Props["action"];
}) {
  const story = chronicleLiteraryText(event, sourceAction);
  return (
    <article className="cc-chronicle-detail-entry">
      <div className="cc-chronicle-detail-meta">
        <span>{chronicleEventLabel(event.eventType)}</span>
        <time>{formatDateTime(event.createdAt)}</time>
      </div>
      <h2 className="cc-chronicle-modal-title">{event.title}</h2>
      {sourceAction && sourceAction.images.length > 0 && (
        <CycleImages
          campaignId={campaignId}
          assets={sourceAction.images}
          label="Иллюстрация записи"
          canManage={canManage}
          action={action}
        />
      )}
      {event.eventType === "DEFERRED_CONSEQUENCE" && event.originAction && (
        <DeferredOriginActionDetails originAction={event.originAction} />
      )}
      {story && <Paragraphs text={story} />}
      {event.nextStepText && (
        <section>
          <h3>Что дальше</h3>
          <Paragraphs text={event.nextStepText} />
        </section>
      )}
    </article>
  );
}
function DeferredOriginActionDetails({
  originAction,
  compact = false,
}: {
  originAction: NonNullable<ChronicleEvent["originAction"]>;
  compact?: boolean;
}) {
  const originText = deduplicateStoryText([
    originAction.description,
    originAction.sceneText,
    originAction.achievementText,
    originAction.effectText,
  ]);
  return (
    <details className={`cc-deferred-origin-action${compact ? " compact" : ""}`}>
      <summary>
        <span>Предыстория действия</span>
        <b>{originAction.title}</b>
      </summary>
      <div>
        <Paragraphs text={originText} />
      </div>
    </details>
  );
}
function ChronicleSyntheticDetails({
  campaignId,
  item,
  canManage,
  action,
}: {
  campaignId: string;
  item: Extract<ChronicleFeedItem, { kind: "prologue" | "cycle-situation" | "cycle-outcome" }>;
  canManage: boolean;
  action: Props["action"];
}) {
  return (
    <article className="cc-chronicle-detail-entry cc-chronicle-synthetic-detail">
      <div className="cc-chronicle-detail-meta">
        <span>{chronicleFeedMeta(item)}</span>
        <time>{formatDateTime(item.createdAt)}</time>
      </div>
      <h2 className="cc-chronicle-modal-title">{item.title}</h2>
      {item.images.length > 0 && (
        <CycleImages
          campaignId={campaignId}
          assets={item.images}
          label="Иллюстрация хроники"
          canManage={canManage}
          action={action}
        />
      )}
      <Paragraphs text={item.body || item.summary} />
    </article>
  );
}
function ChronicleChapterDetails({
  campaignId,
  chapter,
  canManage,
  action,
}: {
  campaignId: string;
  chapter: ChronicleChapter;
  canManage: boolean;
  action: Props["action"];
}) {
  return (
    <article className="cc-chronicle-detail-entry cc-chronicle-chapter-detail">
      <div className="cc-chronicle-detail-meta">
        <span>Глава кампании</span>
        <time>{formatDateTime(chapter.createdAt)}</time>
      </div>
      <h2 className="cc-chronicle-modal-title">{chapter.title}</h2>
      {chapter.recapImages.length > 0 && (
        <CycleImages
          campaignId={campaignId}
          assets={chapter.recapImages}
          label="Комикс-итог главы"
          canManage={canManage}
          action={action}
        />
      )}
      {chapter.openingSummary && (
        <section>
          <h3>С чего всё началось</h3>
          <Paragraphs text={chapter.openingSummary} />
        </section>
      )}
      {chapter.keyEvents.length > 0 && (
        <section>
          <h3>Ключевые события</h3>
          <div className="cc-chronicle-detail-list">
            {chapter.keyEvents.filter((event) => event.eventType !== "DAY_OUTCOME" && hasChapterLiteraryText(event)).map((event) => (
              <article key={event.id}>
                <strong>{event.title}</strong>
                <Paragraphs text={chapterLiteraryText(event)} />
              </article>
            ))}
          </div>
        </section>
      )}
      {chapter.decisions.length > 0 && (
        <section>
          <h3>Решения Совета</h3>
          <div className="cc-chronicle-detail-list">
            {chapter.decisions.map((decision) => (
              <article key={decision.initiativeId}>
                <strong>{decision.title}</strong>
                <span>{chronicleDecisionStatus(decision.status)}</span>
                {decision.explanation && <Paragraphs text={decision.explanation} />}
              </article>
            ))}
          </div>
        </section>
      )}
      {chapter.worldChanges.length > 0 && (
        <section>
          <h3>Что изменилось</h3>
          <div className="cc-chronicle-detail-list compact">
            {chapter.worldChanges.map((change, index) => (
              <article key={`${change.code}-${index}`}>
                <strong>{change.displayName}</strong>
                <span>{worldChangeDescription(change)}</span>
              </article>
            ))}
          </div>
        </section>
      )}
      {chapter.closingNarrative && (
        <section>
          <h3>Итог дня</h3>
          <Paragraphs text={chapter.closingNarrative} />
        </section>
      )}
    </article>
  );
}
function hasChapterLiteraryText(event: ChronicleEvent): boolean {
  return chapterLiteraryText(event).trim().length > 0;
}
function chapterLiteraryText(event: ChronicleEvent): string {
  return deduplicateStoryText([event.narrativeText, event.reactionText]);
}
function chronicleDecisionStatus(status: string): string {
  return (
    {
      ACCEPTED: "Принято",
      REJECTED: "Отклонено",
      NOT_FUNDED: "Не профинансировано",
      RESOLVED: "Реализовано",
      MERGED: "Объединено",
      VOTING: "На голосовании",
      DISCUSSION: "Обсуждение",
      DRAFT: "Черновик",
    }[status] ?? status
  );
}
function worldChangeDescription(changeOrDelta: number | WorldChange) {
  if (typeof changeOrDelta === "number") {
    const delta = changeOrDelta;
    const magnitude = Math.abs(delta);
    if (delta === 0) return "Состояние не изменилось.";
    const degree =
      magnitude >= 10 ? "резко" : magnitude >= 4 ? "заметно" : "немного";
    return delta > 0
      ? `Состояние ${degree} улучшилось.`
      : `Состояние ${degree} ухудшилось.`;
  }
  const change = changeOrDelta;
  const amount = Math.abs(change.delta);
  const arrow = `${change.before} → ${change.after}`;
  if (change.delta === 0) return `Значение не изменилось (${arrow}).`;
  const unit = change.kind === "INDICATOR" ? pointUnit(amount) : resourceUnit(amount);
  switch (change.kind) {
    case "RESOURCE":
      return change.delta > 0
        ? `Запасы выросли на ${amount} ${unit} (${arrow}).`
        : `Запасы снизились на ${amount} ${unit} (${arrow}).`;
    case "INDICATOR":
      return change.delta > 0
        ? `Показатель вырос на ${amount} ${unit} (${arrow}).`
        : `Показатель снизился на ${amount} ${unit} (${arrow}).`;
    case "INSTITUTION_LOAD":
      return change.delta > 0
        ? `Нагрузка выросла на ${amount} (${arrow}).`
        : `Нагрузка снизилась на ${amount} (${arrow}).`;
    case "FACTION_ATTITUDE":
      return change.delta > 0
        ? `Отношение улучшилось на ${amount} (${arrow}).`
        : `Отношение ухудшилось на ${amount} (${arrow}).`;
    default:
      return `Изменение ${change.delta > 0 ? "+" : ""}${change.delta} (${arrow}).`;
  }
}
function resourceUnit(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "единицу";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "единицы";
  return "единиц";
}
function pointUnit(value: number) {
  const mod10 = value % 10;
  const mod100 = value % 100;
  if (mod10 === 1 && mod100 !== 11) return "пункт";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "пункта";
  return "пунктов";
}
function chronicleLiteraryText(
  event: ChronicleEvent,
  sourceAction?: CycleAction,
): string {
  return deduplicateStoryText([
    event.narrativeText,
    event.reactionText,
    sourceAction?.sceneText,
  ]);
}
function deduplicateStoryText(parts: Array<string | undefined>): string {
  const seen = new Set<string>();
  const paragraphs: string[] = [];
  parts
    .map((text) => text?.trim() ?? "")
    .filter(Boolean)
    .forEach((part) => {
      part
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.replace(/\s+/g, " ").trim())
        .filter(Boolean)
        .forEach((paragraph) => {
          const key = storyParagraphKey(paragraph);
          if (seen.has(key)) return;
          seen.add(key);
          paragraphs.push(paragraph);
        });
    });
  return paragraphs.join("\n\n");
}
function storyParagraphKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[«»"“”'.,:;!?—–\-()[\]]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
function chronicleStageLabel(event: ChronicleEvent): string {
  switch (event.eventType) {
    case "SITUATION":
      return "Ситуация";
    case "ACTION":
    case "DEFERRED_CONSEQUENCE":
      return "Действие";
    case "NPC_ARRIVED":
    case "NPC_VISIT_RESOLVED":
    case "NPC_VISIT_MISSED":
      return "NPC";
    case "AGREEMENT":
      return "Договорённость";
    case "INITIATIVE_RESOLVED":
    case "INITIATIVE_NOT_FUNDED":
    case "VOTING_RESULT":
      return "Решение";
    case "WORLD_CHANGE":
      return "Мир";
    case "THREAT_CHANGED":
    case "COLLECTIVE_DEFEAT":
      return "Угроза";
    case "CAMPAIGN_VICTORY":
      return "Финал";
    case "DAY_OUTCOME":
      return "Итог";
    default:
      return "Событие";
  }
}
function chronicleEventLabel(value: ChronicleEvent["eventType"]) {
  return {
    SITUATION: "Ситуация дня",
    MEMBER_JOINED: "Новый участник",
    NPC_ARRIVED: "Прибытие посетителя",
    NPC_VISIT_RESOLVED: "Итог встречи",
    NPC_VISIT_MISSED: "Упущенная встреча",
    PHASE_DEADLINE: "Срок этапа",
    ACTION: "Действие",
    DEFERRED_CONSEQUENCE: "Отложенное последствие",
    AGREEMENT: "Договорённость",
    INITIATIVES_CONSOLIDATED: "Объединение инициатив",
    VOTING_OPENED: "Голосование открыто",
    VOTING_RESULT: "Итог голосования",
    INITIATIVE_NOT_FUNDED: "Решение без финансирования",
    INITIATIVE_RESOLVED: "Результат инициативы",
    WORLD_CHANGE: "Изменение мира",
    THREAT_CHANGED: "Изменение угрозы",
    COLLECTIVE_DEFEAT: "Коллективное поражение",
    CAMPAIGN_VICTORY: "Победа кампании",
    DAY_OUTCOME: "Итог дня",
  }[value];
}
function chronicleEventIcon(value: ChronicleEvent["eventType"]) {
  return {
    SITUATION: "!",
    MEMBER_JOINED: "◉",
    NPC_ARRIVED: "⇥",
    NPC_VISIT_RESOLVED: "◈",
    NPC_VISIT_MISSED: "⌛",
    PHASE_DEADLINE: "⌛",
    ACTION: "◆",
    DEFERRED_CONSEQUENCE: "↳",
    AGREEMENT: "◈",
    INITIATIVES_CONSOLIDATED: "⧉",
    VOTING_OPENED: "⚖",
    VOTING_RESULT: "✓",
    INITIATIVE_NOT_FUNDED: "◇",
    INITIATIVE_RESOLVED: "✦",
    WORLD_CHANGE: "↕",
    THREAT_CHANGED: "!",
    COLLECTIVE_DEFEAT: "×",
    CAMPAIGN_VICTORY: "✓",
    DAY_OUTCOME: "▣",
  }[value];
}
function chronicleEventIconSrc(value: ChronicleEvent["eventType"]): string {
  const file = {
    SITUATION: "icon_chronicle_situation.png",
    MEMBER_JOINED: "icon_chronicle_member_joined.png",
    NPC_ARRIVED: "icon_chronicle_npc_arrived.png",
    NPC_VISIT_RESOLVED: "icon_chronicle_npc_resolved.png",
    NPC_VISIT_MISSED: "icon_chronicle_npc_missed.png",
    PHASE_DEADLINE: "icon_chronicle_deadline.png",
    ACTION: "icon_chronicle_action.png",
    DEFERRED_CONSEQUENCE: "icon_chronicle_deferred.png",
    AGREEMENT: "icon_chronicle_agreement.png",
    INITIATIVES_CONSOLIDATED: "icon_chronicle_initiatives.png",
    VOTING_OPENED: "icon_chronicle_voting_opened.png",
    VOTING_RESULT: "icon_chronicle_voting_result.png",
    INITIATIVE_NOT_FUNDED: "icon_chronicle_not_funded.png",
    INITIATIVE_RESOLVED: "icon_chronicle_initiative_resolved.png",
    WORLD_CHANGE: "icon_chronicle_world_change.png",
    THREAT_CHANGED: "icon_chronicle_threat.png",
    COLLECTIVE_DEFEAT: "icon_chronicle_defeat.png",
    CAMPAIGN_VICTORY: "icon_chronicle_victory.png",
    DAY_OUTCOME: "icon_chronicle_day_outcome.png",
  }[value];
  return `${GAME_UI_ASSET_BASE}/${file}`;
}

export {
  ChronicleSection,
  ChronicleEntryModal,
  ChronicleChapterDetails,
  ChronicleEventDetails,
  ChronicleSyntheticDetails,
  DeferredOriginActionDetails,
  hasChapterLiteraryText,
  chapterLiteraryText,
  chronicleDecisionStatus,
  chronicleLiteraryText,
  deduplicateStoryText,
  storyParagraphKey,
  chronicleFeedMeta,
  chronicleStageLabel,
  chronicleEventLabel,
  chronicleEventIcon,
  chronicleEventIconSrc,
  worldChangeDescription,
  resourceUnit,
  pointUnit,
  WorldComicStrip,
  WorldComicThumb,
  WorldComicLightbox,
};
